/**
 * Landing hero — per-element "Style × Color" style-constant maps.
 *
 * - Style = a *colorless* className built from literal `primary` utility + motion classes.
 *   Color is injected separately via `tint()` (an inline `--primary`/`--ring` override), so
 *   any style maps onto any color.
 * - The shipped sections reference only a few chosen entries; the rest are kept for now
 *   (slimming to only the chosen keys is a follow-up). The only custom motion class still
 *   wired up in `app/globals.css` is `landing-ping` (chosen 5-step icons).
 *
 * Pure data / strings (no JSX).
 */

import type { CSSProperties } from "react"

/* ----------------------------------------------------------------- color ---- */

/** Shared accent palette. `green` = no tint (inherits the real `--primary`, theme-aware). */
export const HERO_PALETTE: { value: string; label: string; color?: string }[] = [
  { value: "green", label: "Green (default)" },
  { value: "donistaBlue", label: "donista Blue", color: "var(--chart-4)" }, // #3276E4 true blue
  { value: "brandBlue", label: "Brand Blue", color: "#0071e3" },
  { value: "donistaRed", label: "donista Red", color: "#af1e2d" },
  { value: "logoRed", label: "Logo Red", color: "#9c242e" },
  { value: "catRed", label: "Cat · Red", color: "#ef4444" },
  { value: "catBlue", label: "Cat · Blue", color: "#3b82f6" },
  { value: "catPink", label: "Cat · Pink", color: "#ec4899" },
  { value: "catPurple", label: "Cat · Purple", color: "#a855f7" },
  { value: "catOrange", label: "Cat · Orange", color: "#f97316" },
  { value: "catRose", label: "Cat · Rose", color: "#fb7185" },
  { value: "catTeal", label: "Cat · Teal", color: "#14b8a6" },
  { value: "catEmerald", label: "Cat · Emerald", color: "#16a34a" },
  { value: "catIndigo", label: "Cat · Indigo", color: "#2563eb" },
  { value: "catViolet", label: "Cat · Violet", color: "#9333ea" },
  { value: "catCyan", label: "Cat · Cyan", color: "#06b6d4" },
  { value: "catLime", label: "Cat · Lime", color: "#84cc16" },
  { value: "catDarkOrange", label: "Cat · Dark Orange", color: "#ea580c" },
  { value: "gold", label: "Gold", color: "#F7C230" },
]

export const COLOR_OPTIONS = HERO_PALETTE.map((p) => ({ value: p.value, label: p.label }))

export const paletteColor = (v: string): string | undefined => HERO_PALETTE.find((p) => p.value === v)?.color

/** Inline accent override; `undefined` for green so the element keeps the real theme token. */
export const tint = (c?: string): CSSProperties | undefined =>
  c ? ({ "--primary": c, "--ring": c } as CSSProperties) : undefined

/* ----------------------------------------------------------------- styles --- */

/** Solid-fill buttons (CTA, "Kostenlos starten"). Override the default Button variant via tailwind-merge. */
export const BUTTON_STYLES: Record<string, string> = {
  solid: "bg-primary text-primary-foreground hover:bg-primary/90",
  solidShadow: "bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90",
  outline: "border-2 border-primary bg-transparent text-primary shadow-none hover:bg-primary/5",
  outlineFill:
    "border-2 border-primary bg-transparent text-primary shadow-none transition-colors hover:bg-primary hover:text-primary-foreground",
  ghost: "bg-transparent text-primary shadow-none hover:bg-primary/10",
  soft: "bg-primary/10 text-primary shadow-none hover:bg-primary/20",
  glow: "bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-shadow hover:shadow-xl hover:shadow-primary/50",
  lift: "bg-primary text-primary-foreground shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg",
  pressable: "bg-primary text-primary-foreground transition-transform active:scale-95",
  ringExpand: "bg-primary text-primary-foreground transition-shadow hover:ring-4 hover:ring-primary/30",
  gradient: "border-0 text-white shadow-none bg-gradient-to-r from-primary to-[var(--chart-4)] transition-opacity hover:opacity-90",
  gradientAnimated:
    "border-0 text-white shadow-none bg-gradient-to-r from-primary via-[var(--chart-4)] to-primary bg-[length:200%_auto] dl-gradient-pan",
  shimmer: "relative overflow-hidden bg-primary text-primary-foreground dl-shimmer",
  pill: "rounded-full bg-primary text-primary-foreground hover:bg-primary/90",
  pillOutlineFill:
    "rounded-full border-2 border-primary bg-transparent text-primary shadow-none transition-colors hover:bg-primary hover:text-primary-foreground",
}

/** Icon-only button (search submit) — motion-focused. */
export const ICON_BUTTON_STYLES: Record<string, string> = {
  solid: "bg-primary text-primary-foreground hover:bg-primary/90 transition-transform hover:scale-105",
  soft: "bg-primary/10 text-primary hover:bg-primary/20",
  outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary/5",
  outlineFill:
    "border-2 border-primary bg-transparent text-primary transition-colors hover:bg-primary hover:text-primary-foreground",
  glow: "bg-primary text-primary-foreground transition-shadow hover:shadow-lg hover:shadow-primary/50",
  pulse: "bg-primary text-primary-foreground animate-pulse",
  ringExpand: "bg-primary text-primary-foreground transition-shadow hover:ring-4 hover:ring-primary/30",
  spinHover: "bg-primary text-primary-foreground [&_svg]:transition-transform hover:[&_svg]:rotate-90",
  bounceHover: "bg-primary text-primary-foreground transition-transform hover:scale-110",
  gradient: "border-0 text-white bg-gradient-to-br from-primary to-[var(--chart-4)] hover:opacity-90",
  pill: "rounded-full bg-primary text-primary-foreground hover:bg-primary/90",
}

/** Search-bar input field shape. */
export const SEARCHBAR_STYLES: Record<string, string> = {
  default: "rounded-xl",
  pill: "rounded-full",
}

/** "Unser Angebot" menu-item button shape (merged over the base button classes via `cn`).
   Active/idle border + bg are handled by the component; these only swap the shape. */
export const MENU_ITEM_STYLES: Record<string, string> = {
  default: "rounded-xl",
  pill: "rounded-full",
  outline: "rounded-full border-2",
  underline: "rounded-none border-0 border-b-2",
  soft: "rounded-2xl",
}

/** Headline accent word. */
export const TEXT_STYLES: Record<string, string> = {
  plain: "text-primary",
  extrabold: "text-primary font-extrabold",
  underline: "text-primary underline decoration-primary decoration-2 underline-offset-4",
  underlineThick: "text-primary underline decoration-primary decoration-4 underline-offset-4",
  wavy: "text-primary underline decoration-primary decoration-wavy decoration-2 underline-offset-4",
  dotted: "text-primary underline decoration-primary decoration-dotted decoration-2 underline-offset-4",
  underlineGrow: "text-primary dl-underline-grow",
  highlight: "text-foreground bg-primary/15 box-decoration-clone rounded px-1.5",
  highlightSolid: "text-primary-foreground bg-primary box-decoration-clone rounded-lg px-3",
  highlightSweep: "text-foreground dl-highlight-sweep box-decoration-clone rounded px-1.5",
  gradientText: "bg-gradient-to-r from-primary to-[var(--chart-4)] bg-clip-text text-transparent",
  gradientTextAnimated:
    "bg-gradient-to-r from-primary via-[var(--chart-4)] to-primary bg-[length:200%_auto] bg-clip-text text-transparent dl-gradient-pan",
  shine: "bg-gradient-to-r from-primary via-white to-primary bg-[length:200%_auto] bg-clip-text text-transparent dl-gradient-pan",
  boxed: "text-primary border-2 border-primary rounded-lg px-2",
  pill: "text-primary bg-primary/10 rounded-full px-3",
}

/** Step icon circles. Appended to each band variant's existing size classes. */
export const CIRCLE_STYLES: Record<string, string> = {
  solid: "bg-primary text-primary-foreground",
  solidShadow: "bg-primary text-primary-foreground shadow-lg shadow-primary/30",
  glow: "bg-primary text-primary-foreground transition-shadow hover:shadow-xl hover:shadow-primary/50",
  outline: "border-2 border-primary bg-background text-primary",
  outlineFill: "border-2 border-primary bg-background text-primary transition-colors hover:bg-primary hover:text-primary-foreground",
  soft: "bg-primary/10 text-primary",
  ring: "bg-background text-primary ring-2 ring-primary",
  ringExpand: "bg-primary text-primary-foreground transition-shadow hover:ring-4 hover:ring-primary/30",
  pulse: "bg-primary text-primary-foreground animate-pulse",
  ping: "bg-primary text-primary-foreground landing-ping",
  lift: "bg-primary text-primary-foreground transition-transform hover:-translate-y-1",
  bounceHover: "bg-primary text-primary-foreground transition-transform hover:scale-110",
  spinHover: "bg-primary text-primary-foreground [&_svg]:transition-transform hover:[&_svg]:rotate-12",
  gradient: "text-white bg-gradient-to-br from-primary to-[var(--chart-4)]",
  gradientAnimated: "text-white bg-gradient-to-br from-primary via-[var(--chart-4)] to-primary bg-[length:200%_200%] dl-gradient-pan",
  wiggleHover: "bg-primary text-primary-foreground dl-wiggle-hover",
}

/** Field-of-work chips (applied to the SELECTED branch only). */
export const PILL_STYLES: Record<string, string> = {
  soft: "border border-primary bg-primary/10 text-primary",
  solid: "border border-primary bg-primary text-primary-foreground",
  outline: "border-2 border-primary bg-transparent text-primary",
  outlineFill: "border-2 border-primary bg-transparent text-primary transition-colors hover:bg-primary hover:text-primary-foreground",
  ghost: "border-transparent bg-transparent text-primary",
  pillShadow: "border border-primary bg-primary/10 text-primary shadow-md shadow-primary/20",
  ring: "border-transparent bg-background text-primary ring-2 ring-primary",
  dashed: "border-2 border-dashed border-primary bg-transparent text-primary",
  lift: "border border-primary bg-primary/10 text-primary transition-transform hover:-translate-y-0.5",
  glow: "border border-primary bg-primary/10 text-primary transition-shadow hover:shadow-lg hover:shadow-primary/40",
  pulse: "border border-primary bg-primary/10 text-primary animate-pulse",
  gradient: "border-0 text-white bg-gradient-to-r from-primary to-[var(--chart-4)]",
}

/** Donation amount cards — card + figure styled separately. */
export const BOX_STYLES: Record<string, { box: string; figure: string }> = {
  soft: { box: "border border-primary/20 bg-primary/5", figure: "text-primary" },
  solid: { box: "border border-primary bg-primary", figure: "text-primary-foreground" },
  outline: { box: "border-2 border-primary bg-transparent", figure: "text-primary" },
  plain: { box: "border-0 bg-transparent", figure: "text-primary" },
  gradientFigure: {
    box: "border border-primary/20 bg-primary/5",
    figure: "bg-gradient-to-r from-primary to-[var(--chart-4)] bg-clip-text text-transparent",
  },
  glow: { box: "border border-primary/20 bg-primary/5 shadow-lg shadow-primary/20", figure: "text-primary" },
  ring: { box: "border-0 bg-background ring-2 ring-primary", figure: "text-primary" },
  pill: { box: "border border-primary/20 bg-primary/5 rounded-full", figure: "text-primary" },
  lift: { box: "border border-primary/20 bg-primary/5 transition-transform hover:-translate-y-0.5", figure: "text-primary" },
  pulseFigure: { box: "border border-primary/20 bg-primary/5", figure: "text-primary animate-pulse" },
  solidGradient: { box: "border-0 bg-gradient-to-br from-primary to-[var(--chart-4)]", figure: "text-white" },
}

/** Slider — child-target arbitrary variants on the `<Slider>` root (`data-slot=slider-track|range|thumb`). */
export const SLIDER_STYLES: Record<string, string> = {
  default: "",
  thickTrack: "[&_[data-slot=slider-track]]:h-2.5",
  thinTrack: "[&_[data-slot=slider-track]]:h-0.5",
  bigThumb: "[&_[data-slot=slider-thumb]]:size-6",
  squareThumb: "[&_[data-slot=slider-thumb]]:rounded-md",
  filledThumb: "[&_[data-slot=slider-thumb]]:bg-primary",
  glowThumb: "[&_[data-slot=slider-thumb]]:shadow-lg [&_[data-slot=slider-thumb]]:shadow-primary/50",
  pulseThumb: "[&_[data-slot=slider-thumb]]:animate-pulse",
  ringThumb: "[&_[data-slot=slider-thumb]]:ring-2 [&_[data-slot=slider-thumb]]:ring-primary",
  growThumbHover: "[&_[data-slot=slider-thumb]]:transition-transform [&_[data-slot=slider-thumb]]:hover:scale-125",
  gradientRange:
    "[&_[data-slot=slider-range]]:bg-gradient-to-r [&_[data-slot=slider-range]]:from-primary [&_[data-slot=slider-range]]:to-[var(--chart-4)]",
  animatedGradientRange:
    "[&_[data-slot=slider-range]]:bg-gradient-to-r [&_[data-slot=slider-range]]:from-primary [&_[data-slot=slider-range]]:via-[var(--chart-4)] [&_[data-slot=slider-range]]:to-primary [&_[data-slot=slider-range]]:bg-[length:200%_auto] dl-anim-range",
}

/** Friendly labels for style keys (shared across maps; same key → same meaning). */
export const STYLE_LABELS: Record<string, string> = {
  // shared
  solid: "Solid",
  solidShadow: "Solid + shadow",
  outline: "Outline",
  outlineFill: "Outline → fill",
  ghost: "Ghost",
  soft: "Soft tint",
  glow: "Glow on hover",
  lift: "Lift on hover",
  pressable: "Pressable",
  ringExpand: "Ring expand",
  gradient: "Gradient",
  gradientAnimated: "Gradient (animated)",
  shimmer: "Shimmer sweep",
  pill: "Pill",
  pillOutlineFill: "Pill outline → fill",
  segmented: "Segmented",
  pulse: "Pulse",
  spinHover: "Spin icon",
  bounceHover: "Bounce",
  // text
  plain: "Plain",
  extrabold: "Extra bold",
  underline: "Underline",
  underlineThick: "Underline (thick)",
  wavy: "Wavy underline",
  dotted: "Dotted underline",
  underlineGrow: "Underline grow",
  highlight: "Highlight",
  highlightSolid: "Highlight (solid)",
  highlightSweep: "Highlight sweep",
  gradientText: "Gradient text",
  gradientTextAnimated: "Gradient text (animated)",
  shine: "Shine",
  boxed: "Boxed",
  // circle
  ring: "Ring",
  ping: "Ping halo",
  wiggleHover: "Wiggle",
  // pill
  pillShadow: "Soft + shadow",
  dashed: "Dashed",
  // box
  plain_box: "Plain",
  gradientFigure: "Gradient figure",
  pulseFigure: "Pulse figure",
  solidGradient: "Solid gradient",
  // slider
  default: "Default",
  thickTrack: "Thick track",
  thinTrack: "Thin track",
  bigThumb: "Big thumb",
  squareThumb: "Square thumb",
  filledThumb: "Filled thumb",
  glowThumb: "Glow thumb",
  pulseThumb: "Pulse thumb",
  ringThumb: "Ring thumb",
  growThumbHover: "Grow thumb",
  gradientRange: "Gradient range",
  animatedGradientRange: "Gradient range (animated)",
}

/** Build `{value,label}` options from a style map's keys. */
export const styleOptions = (map: Record<string, unknown>) =>
  Object.keys(map).map((k) => ({ value: k, label: STYLE_LABELS[k] ?? k }))
